import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { User, CheckCircle, XCircle } from 'lucide-react';
import { getRoleLabel, getRoleBadgeColor } from './constants';
import SignatureBlock from './SignatureBlock';

/**
 * Carte d'un invité (financeur, référent, intervenant externe...) sur la
 * feuille d'émargement d'un atelier. Boutons de présence + bloc de signature
 * réutilisable.
 *
 * Note : showSignaturePad / setShowSignaturePad / signatureRef ne sont plus
 * requis depuis la migration vers SignatureBlock (gère son propre état).
 */
const InviteCard = ({
  invite,
  onPresenceChange,
  onSaveSignature,
  onClearSignature,
}) => {
  return (
    <Card className={`border-2 transition-colors ${invite.present ? 'bg-emerald-50 border-emerald-200' : 'bg-purple-50 border-purple-200'}`}>
      <CardContent className="p-4">
        <div className="flex flex-col gap-3">
          {/* Info invité */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <span className="font-semibold text-slate-900">
                  {invite.prenom} {invite.nom}
                </span>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={getRoleBadgeColor(invite.role)}>
                    {getRoleLabel(invite.role)}
                  </Badge>
                  {invite.statut_confirmation === 'confirme' && (
                    <Badge className="bg-emerald-100 text-emerald-700">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Confirmé
                    </Badge>
                  )}
                  {invite.statut_confirmation === 'decline' && (
                    <Badge className="bg-red-100 text-red-700">
                      <XCircle className="w-3 h-3 mr-1" />
                      Décliné
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Bouton présence */}
            <div className="flex items-center gap-3">
              <Label className="text-sm">Présent :</Label>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={invite.present ? "default" : "outline"}
                  className={`rounded-xl ${invite.present ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                  onClick={() => onPresenceChange(invite.id, true)}
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Oui
                </Button>
                <Button
                  size="sm"
                  variant={!invite.present ? "default" : "outline"}
                  className={`rounded-xl ${!invite.present ? 'bg-red-600 hover:bg-red-700' : ''}`}
                  onClick={() => onPresenceChange(invite.id, false)}
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Non
                </Button>
              </div>
            </div>
          </div>

          {/* Signature de l'invité (si présent) */}
          {invite.present && (
            <div className="pl-13 border-t pt-3 mt-2">
              <SignatureBlock
                signature={invite.signature}
                onSave={(data) => onSaveSignature && onSaveSignature(`invite_${invite.id}`, data)}
                onClear={() => onClearSignature && onClearSignature(`invite_${invite.id}`)}
                signedLabel="Signature manuscrite enregistrée"
                primaryColor="bg-purple-600 hover:bg-purple-700"
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default InviteCard;
